import React from "react"

import { gql, useSubscription, useMutation } from "@apollo/client"
import { useParams } from "react-router-dom"

import { Grid, CircularProgress } from "@mui/material"

import FarmMap from "./FarmMap"
import AddFarmArea from "./AddFarmArea"
import { farmAreaToGeoJSON, growingLineToGeoJSON, marksToGeoJSON } from "../../utils/geoJson"
import AddFarmLinesIndex from "./AddFarmLines"
import AddMarksIndex from "./AddMarks"
import AreaDetail from "./AreaDetail.js/AreaDetail"
import FloatingActionButtons from "../../components/Map/FloatingActionButtons"

const GET_FARM_AREAS = gql`
  subscription GetFarmAreas($farm_id: Int!) {
    farm(
      where: {
        _and: [{ id: { _eq: $farm_id } }, { archived_at: { _is_null: true } }]
      }
    ) {
      name
      id
      geo_location {
        geo_json
        id
      }
      areas(where: { archived_at: { _is_null: true } }) {
        description
        id
        geo_location {
          geo_json
          id
        }
        geographic_features(where: { _and: [{ type: { _in: ["growing_line", "marker_point"] } }, { archived_at: { _is_null: true } }] }) {
          id
          name
          type
          geometry_type
          geo_json
          metadata
        }
      }
    }
  }
`

const ARCHIVE_FARM_AREA = gql`
  mutation ArchiveArea($areaId: Int!) {
    update_area(
      where: { id: { _eq: $areaId } }
      _set: { archived_at: "now()" }
    ) {
      returning {
        id
      }
    }
  }
`

const ARCHIVE_GEO_LOCATION = gql`
  mutation ArchiveGeoLocation($geoLocationId: Int!) {
    update_geo_location(
      where: { id: { _eq: $geoLocationId } }
      _set: { archived_at: "now()" }
    ) {
      returning {
        id
      }
    }
  }
`

export default function Farm() {
  const { id: farm_id } = useParams()
  const [addingArea, setAddingArea] = React.useState(false)
  const [addingLines, setAddingLines] = React.useState(false)
  const [addingMarks, setAddingMarks] = React.useState(false)
  const [activeArea, setActiveArea] = React.useState(null)
  const [activeLine, setActiveLine] = React.useState(null)
  const [activeMark, setActiveMark] = React.useState(null)
  const [archiveArea] = useMutation(ARCHIVE_FARM_AREA)
  const [archiveGeoLocation] = useMutation(ARCHIVE_GEO_LOCATION)
  const { data, loading, error } = useSubscription(GET_FARM_AREAS, {
    variables: { farm_id },
  })
  if (loading) return <CircularProgress />
  if (error) return <div>There was an error connecting to the server</div>

  const growingLinesGeoJSON = growingLineToGeoJSON(
    data?.farm[0]?.areas,
    activeLine
  )

  const marksGeoJSON = marksToGeoJSON(
    data?.farm[0]?.areas,
    activeMark
  )

  let farmAreaGeoJSON = farmAreaToGeoJSON(data?.farm[0]?.areas, activeArea, activeLine, activeMark)

  //strip the leading ( and trailing ) from the string
  const lngLat = data?.farm[0]?.geo_location?.geo_json?.coordinates
  const initialCenter = lngLat ? {
    latitude: lngLat[1],
    longitude: lngLat[0],
  } : {
    latitude: 42.361,
    longitude: -71.057,
  }

  return (
    <div style={{ margin: "-24px 0 0 0" }}>
      <AddFarmArea
        open={addingArea}
        setAddingArea={setAddingArea}
        farmId={farm_id}
        farmAreaGeoJSON={farmAreaGeoJSON}
      />
      <AddFarmLinesIndex
        open={addingLines}
        setAddingLines={setAddingLines}
        activeAreaId={activeArea}
        farmAreaGeoJSON={farmAreaGeoJSON}
        initialCenter={initialCenter}
        growingLinesGeoJSON={growingLinesGeoJSON}
      />
      <AddMarksIndex
        open={addingMarks}
        setAddingMarks={setAddingMarks}
        activeAreaId={activeArea}
        farmAreaGeoJSON={farmAreaGeoJSON}
        initialCenter={initialCenter}
        marksGeoJSON={marksGeoJSON}
      />
      <Grid container spacing={0} sx={{ margin: 0 }}>
        <Grid item md={activeArea ? 4 : 0} xs={12} sx={{ padding: 0 }}>
          {activeArea && (
            <AreaDetail 
              activeArea={activeArea} 
              activeLine={activeLine} 
              activeMark={activeMark}
              onArchiveArea={(areaId) => {
                archiveArea({
                  variables: { areaId },
                })
                setActiveArea(null)
              }}
              onArchiveLine={(geoLocationId) => {
                archiveGeoLocation({
                  variables: { geoLocationId },
                })
                setActiveLine(null)
              }}
              onArchiveMark={(geoLocationId) => {
                archiveGeoLocation({
                  variables: { geoLocationId },
                })
                setActiveMark(null)
              }}
            />
          )}
        </Grid>
        <Grid item md={activeArea ? 8 : 12} xs={12} sx={{ position: "relative", padding: 0 }}>
          <FloatingActionButtons
            onAddArea={() => setAddingArea(true)}
            onAddLines={() => setAddingLines(true)}
            onAddMarks={() => setAddingMarks(true)}
            activeArea={activeArea}
          />
          <FarmMap
            initialCenter={initialCenter}
            farmAreaGeoJSON={farmAreaGeoJSON}
            farmAreaData={data?.farm[0]?.areas}
            growingLinesGeoJSON={growingLinesGeoJSON}
            marksGeoJSON={marksGeoJSON}
            setActiveArea={setActiveArea}
            setActiveLine={setActiveLine}
            setActiveMark={setActiveMark}
            activeLine={activeLine}
            activeMark={activeMark}
            activeArea={activeArea}
          />
        </Grid>
      </Grid>
    </div>
  )
}
